import type { NotificationType } from '@shopgnd/shared';
import { getAuthedDataClient } from '@/lib/amplify-client';
import { subscribeAmplify } from '@/lib/amplifySubscribe';

export type InAppNotification = {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body?: string;
  link?: string;
  isRead: boolean;
  createdAt?: string;
};

function mapRow(row: Record<string, unknown>): InAppNotification {
  return {
    id: String(row.id),
    userId: String(row.userId),
    type: row.type as NotificationType,
    title: String(row.title),
    body: row.body ? String(row.body) : undefined,
    link: row.link ? String(row.link) : undefined,
    isRead: Boolean(row.isRead),
    createdAt: row.createdAt ? String(row.createdAt) : undefined,
  };
}

export async function listMyNotifications(userId: string): Promise<InAppNotification[]> {

  const client = await getAuthedDataClient();
  if (!client.models?.Notification?.list) return [];

  const res = await client.models.Notification.list({
    filter: { userId: { eq: userId } },
    limit: 50,
  });

  const rows = (res.data ?? []) as Record<string, unknown>[];
  return rows.map(mapRow).sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

export async function markNotificationRead(id: string): Promise<void> {
  const client = await getAuthedDataClient();
  if (!client.models?.Notification?.update) return;
  await client.models.Notification.update({ id, isRead: true });
}

export async function markAllNotificationsRead(userId: string, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const client = await getAuthedDataClient();
  if (!client.models?.Notification?.update) return;
  await Promise.all(ids.map((id) => client.models.Notification.update({ id, isRead: true })));
}

/** Live updates via AppSync observeQuery when the Notification model is deployed. */
export function subscribeToMyNotifications(
  userId: string,
  onData: (items: InAppNotification[]) => void,
): () => void {

  let cancelled = false;
  let unsubscribe: (() => void) | undefined;

  void (async () => {
    try {
      const client = await getAuthedDataClient();
      if (cancelled || !client.models?.Notification?.observeQuery) return;

      const sub = subscribeAmplify(
        client.models.Notification.observeQuery({
          filter: { userId: { eq: userId } },
        }),
        {
          next: ({ items }: { items: Record<string, unknown>[] }) => {
            const mapped = items.map(mapRow).sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
            onData(mapped);
          },
        },
        'Notification.observeQuery',
      );
      unsubscribe = () => sub.unsubscribe();
    } catch {
      /* model not deployed yet */
    }
  })();

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
